import ConversationsOptionState from '../store/schema/ConversationsOptionState';
import store from '../store/store';
import { logUsage } from 'owa-analytics';
import onConversationOptionSaved from 'owa-options-actions/lib/onConversationOptionSaved';
import { onListViewTypeOptionSaved } from 'owa-options-actions/lib/onListViewTypeOptionSaved';
import { updateUserConfiguration, UserConfigurationSetting } from 'owa-options-core';
import ConversationSortOrder from 'owa-service/lib/contract/ConversationSortOrder';
import ReactListViewType from 'owa-service/lib/contract/ReactListViewType';
import UserOptionsType from 'owa-service/lib/contract/UserOptionsType';
import { getUserConfiguration } from 'owa-session-store';
import { action, select } from 'satcheljs/lib/legacy';

export interface SaveConversationsOptionState {
    optionState?: ConversationsOptionState;
    userOptions: UserOptionsType;
}

let saveConversationsOption = action('saveConversationsOption')(
    async function saveConversationsOption(
        newListViewType: ReactListViewType,
        conversationSortOrder: ConversationSortOrder,
        hideDeletedItems: boolean,
        userOptions: UserOptionsType
    ) {
        let serviceUpdates: UserConfigurationSetting[] = [];

        if (newListViewType !== null && newListViewType !== userOptions.GlobalListViewTypeReact) {
            userOptions.GlobalListViewTypeReact = newListViewType;
            onListViewTypeOptionSaved(newListViewType);

            serviceUpdates.push({
                propertyName: 'GlobalListViewTypeReact',
                propertyType: 'Integer32',
                propertyValue: `${newListViewType}`,
            });
        }

        if (conversationSortOrder) {
            userOptions.ConversationSortOrderReact = conversationSortOrder;

            // The setting is actually stored as an int on the server, despite being sent down as a string
            // Copied from //depot/utah/sources/dev/clients/src/owa/bin/core/ConversationSortOrderReact.cs
            let sortOrderValue;
            switch (userOptions.ConversationSortOrderReact) {
                case 'Chronological':
                    sortOrderValue = 0x1;
                    break;
                case 'Tree':
                    sortOrderValue = 0x2;
                    break;
                case 'NewestOnTop':
                    sortOrderValue = 0x4;
                    break;
                case 'NewestOnBottom':
                    sortOrderValue = 0x8;
                    break;
                case 'ChronologicalNewestOnTop':
                    sortOrderValue = 0x5;
                    break;
                case 'ChronologicalNewestOnBottom':
                    sortOrderValue = 0x9;
                    break;
                case 'TreeNewestOnBottom':
                    sortOrderValue = 0xa;
                    break;
            }

            serviceUpdates.push({
                propertyName: 'ConversationSortOrderReact',
                propertyType: 'Integer32',
                propertyValue: `${sortOrderValue}`,
            });
        }

        if (hideDeletedItems !== null) {
            userOptions.HideDeletedItems = hideDeletedItems;
            serviceUpdates.push({
                propertyName: 'HideDeletedItems',
                propertyType: 'Boolean',
                propertyValue: `${userOptions.HideDeletedItems}`,
            });
        }

        onConversationOptionSaved();

        if (serviceUpdates.length) {
            await updateUserConfiguration(serviceUpdates);
        }
    }
);

export let saveConversationsOptionFull = select<SaveConversationsOptionState>({
    userOptions: () => getUserConfiguration().UserOptions,
    optionState: () => store,
})(
    action('saveConversationsOptionFull')(async function saveConversationsOptionFull(
        state?: SaveConversationsOptionState
    ) {
        let { userOptions, optionState } = state;

        await saveConversationsOption(
            optionState.globalListViewType,
            optionState.conversationSortOrder,
            optionState.hideDeletedItems,
            userOptions
        );
    })
);

const OPTION_QUICK_SETTING_USAGE_KEY = 'QuickSettingConversations';

export const saveConversationsOptionQuick = select<SaveConversationsOptionState>({
    userOptions: () => getUserConfiguration().UserOptions,
})(
    action('saveConversationsOptionQuick')(function saveConversationsOptionQuick(
        isConversationViewEnabled: boolean,
        newSortOrder?: ConversationSortOrder,
        state?: SaveConversationsOptionState
    ) {
        logUsage(OPTION_QUICK_SETTING_USAGE_KEY, [newSortOrder]);
        saveConversationsOption(
            isConversationViewEnabled ? ReactListViewType.Conversation : ReactListViewType.Message,
            newSortOrder,
            null,
            state.userOptions
        );
    })
);
